import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowLeft,
  Eye,
  EyeOff,
  Globe,
  List,
  Loader2,
  Mail,
  Plug,
  RefreshCw,
  Save,
  Shield,
  Upload,
} from 'lucide-react';
import {
  getSystemConfigCategory,
  revealSystemConfig,
  updateSystemConfig,
} from '../../Services/AdminService';
import {
  SystemConfigCategoryResponse,
  SystemConfigDataType,
  SystemConfigEntryResponse,
} from '../../Types/AdminType';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';

const CATEGORY_META: Record<
  string,
  { label: string; description: string; icon: React.ReactNode; accent: string }
> = {
  security: {
    label: 'Security',
    description: 'Tokens, lockout, cookies and seeding policy',
    icon: <Shield size={22} />,
    accent: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  },
  integrations: {
    label: 'Integrations',
    description: 'OpenSearch, Loki and third-party endpoints',
    icon: <Plug size={22} />,
    accent: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
  },
  email: {
    label: 'Email',
    description: 'Provider selection and sender credentials',
    icon: <Mail size={22} />,
    accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
  },
  observability: {
    label: 'Observability',
    description: 'Request logging and actuator exposure',
    icon: <Activity size={22} />,
    accent: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  },
  upload: {
    label: 'File Upload',
    description: 'Multipart size limits',
    icon: <Upload size={22} />,
    accent: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  },
  pagination: {
    label: 'Pagination',
    description: 'Default and maximum page sizes',
    icon: <List size={22} />,
    accent: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-400',
  },
  cors: {
    label: 'CORS',
    description: 'Cross-origin request policy',
    icon: <Globe size={22} />,
    accent: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
  },
};

function isMasked(value: string) {
  return value.length > 0 && value.split('').every((c) => c === '•');
}

const EMAIL_GROUP_ORDER = ['Provider selection', 'Mailtrap', 'ZeptoMail', 'MailHog', 'Common'];

function getEmailGroup(key: string): string {
  if (key === 'email.provider') return 'Provider selection';
  if (key.startsWith('mailtrap')) return 'Mailtrap';
  if (key.startsWith('zepto') || key === 'email.webhook.secret') return 'ZeptoMail';
  if (key.startsWith('RJP.mailhog')) return 'MailHog';
  return 'Common';
}

export default function SystemConfigDetail() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const meta = CATEGORY_META[category || ''];

  const [data, setData] = useState<SystemConfigCategoryResponse | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const showAlert = (variant: AlertProps['variant'], heading: string, message: string) =>
    setAlert({ variant, title: heading, message, autoCloseMs: 6000, onClose: () => setAlert(null) });

  const fetchCategory = useCallback(async () => {
    if (!category) return;
    setLoading(true);
    try {
      const response = await getSystemConfigCategory(category.toUpperCase());
      if (response.success && response.data) {
        setData(response.data);
        const initial: Record<string, string> = {};
        response.data.entries.forEach((entry) => {
          initial[entry.key] = entry.value;
        });
        setValues(initial);
      } else {
        showAlert('error', 'Load Error', response.message || 'Failed to load configuration.');
      }
    } catch {
      showAlert('error', 'Load Error', 'Failed to load configuration.');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  const groupedEntries = useMemo(() => {
    if (!data) return [];
    if (category !== 'email') {
      return [{ group: null as string | null, entries: data.entries }];
    }
    const map: Record<string, SystemConfigEntryResponse[]> = {};
    data.entries.forEach((entry) => {
      const group = getEmailGroup(entry.key);
      if (!map[group]) map[group] = [];
      map[group].push(entry);
    });
    return EMAIL_GROUP_ORDER
      .filter((group) => map[group]?.length > 0)
      .map((group) => ({ group, entries: map[group] }));
  }, [data, category]);

  const dirtyEntries = useMemo(() => {
    if (!data) return [];
    return data.entries.filter((entry) => {
      const current = values[entry.key] ?? '';
      if (entry.dataType === 'SECRET' && isMasked(current)) return false;
      return current !== entry.value;
    });
  }, [data, values]);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleReveal = async (entry: SystemConfigEntryResponse) => {
    if (revealed[entry.key] !== undefined) {
      setValues((prev) => ({ ...prev, [entry.key]: revealed[entry.key] }));
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[entry.key];
        return next;
      });
      return;
    }

    if (!category) return;
    try {
      const response = await revealSystemConfig(category.toUpperCase(), entry.key);
      if (response.success && response.data) {
        setRevealed((prev) => ({ ...prev, [entry.key]: values[entry.key] ?? entry.value }));
        setValues((prev) => ({ ...prev, [entry.key]: response.data.value }));
      } else {
        showAlert('error', 'Reveal Error', response.message || 'Could not reveal secret.');
      }
    } catch {
      showAlert('error', 'Reveal Error', 'Could not reveal secret.');
    }
  };

  const handleSave = async () => {
    if (!category || dirtyEntries.length === 0) return;
    setSaving(true);

    try {
      const results = await Promise.all(
        dirtyEntries.map((entry) =>
          updateSystemConfig(category.toUpperCase(), entry.key, values[entry.key] ?? '')
        )
      );

      const failed = results.filter((r) => !r.success);
      if (failed.length > 0) {
        showAlert('error', 'Save Error', failed[0].message || 'One or more values failed to save.');
      } else {
        showAlert('success', 'Saved', 'Configuration updated successfully.');
        setRevealed({});
        await fetchCategory();
      }
    } catch {
      showAlert('error', 'Save Error', 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (entry: SystemConfigEntryResponse) => {
    const value = values[entry.key] ?? '';
    const sourceBadge = (
      <span
        className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
          entry.source === 'DB'
            ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
            : 'border-slate-700 bg-slate-800 text-slate-400'
        }`}
      >
        {entry.source === 'DB' ? 'DB' : 'Default'}
      </span>
    );

    if (entry.dataType === 'BOOLEAN') {
      return (
        <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-3 transition hover:border-cyan-500/30">
          <input
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => handleChange(entry.key, e.target.checked ? 'true' : 'false')}
            className="h-4 w-4 accent-cyan-500"
          />
          <span className="text-sm font-bold text-slate-200">
            {value === 'true' ? 'Enabled' : 'Disabled'}
          </span>
          {sourceBadge}
        </label>
      );
    }

    if (entry.dataType === 'SECRET') {
      const isRevealed = revealed[entry.key] !== undefined;
      return (
        <div className="relative">
          <input
            type={isRevealed ? 'text' : 'password'}
            value={value}
            placeholder={entry.defaultValue || '••••••'}
            onChange={(e) => handleChange(entry.key, e.target.value)}
            className="w-full rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-2.5 pr-24 text-sm font-semibold text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/50 focus:outline-none"
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {entry.encrypted && (
              <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-400">
                AES
              </span>
            )}
            <button
              type="button"
              onClick={() => handleReveal(entry)}
              className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-cyan-400"
              title={isRevealed ? 'Hide' : 'Reveal'}
            >
              {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="relative">
        <input
          type={entry.dataType === 'NUMBER' ? 'number' : 'text'}
          value={value}
          placeholder={entry.defaultValue}
          onChange={(e) => handleChange(entry.key, e.target.value)}
          className="w-full rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-2.5 pr-16 text-sm font-semibold text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/50 focus:outline-none"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">{sourceBadge}</div>
      </div>
    );
  };

  if (!meta) {
    return (
      <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
        <h1 className="text-xl font-black">Unknown configuration category</h1>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/settings/system-config')}
            className="rounded-xl border border-slate-800 bg-slate-900 p-2.5 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
          >
            <ArrowLeft size={18} />
          </button>
          <span className={`flex h-11 w-11 items-center justify-center rounded-2xl border ${meta.accent}`}>
            {meta.icon}
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">{meta.label}</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">{meta.description}</p>
          </div>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <button
            onClick={fetchCategory}
            title="Refresh"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || dirtyEntries.length === 0}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </button>
        </div>
      </div>

      {alert && (
        <div className="mb-4">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="mx-auto max-w-4xl space-y-8">
          {groupedEntries.map((group, gIdx) => (
            <div key={group.group || 'all'} className="space-y-4">
              {group.group && (
                <h3 className="sticky top-0 z-10 border-b border-slate-800 bg-slate-950/95 pb-2 text-sm font-black uppercase tracking-widest text-slate-300">
                  {group.group}
                </h3>
              )}
              {group.entries.map((entry, idx) => (
                <motion.section
                  key={entry.key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: (gIdx * 0.1) + idx * 0.03 }}
                  className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5"
                >
                  <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 className="text-sm font-bold text-slate-100">{entry.label}</h2>
                      <p className="text-[11px] font-semibold text-slate-500">{entry.description}</p>
                    </div>
                    <code className="mt-1 text-[10px] font-mono text-slate-600 sm:mt-0">{entry.key}</code>
                  </div>
                  {renderField(entry)}
                  {entry.defaultValue && entry.dataType !== 'SECRET' && (
                    <p className="mt-2 text-[10px] font-semibold text-slate-600">
                      Default: {entry.defaultValue}
                    </p>
                  )}
                </motion.section>
              ))}
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {!loading && dirtyEntries.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2"
          >
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-500/40 bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-cyan-400 shadow-[0_0_30px_-8px_rgba(34,211,238,0.5)] transition hover:bg-cyan-500/10 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {dirtyEntries.length} unsaved change{dirtyEntries.length > 1 ? 's' : ''} · Save
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
